import { MetadataAssociatedFile, MetadataGroupInfo, MetadataInfo, MetadataStage } from "../../../utils/dataFormattingFns"
import { ReactComponent as UserIcon } from '../../../assets/Icons/adornments/user-02.svg';
import ExpandJson from "./ExpandJson";
import SelectedItem from "../../Form/Upload/SelectedItem";
import { ReactNode } from "react";
import MetadataEditableMenuItem from "./MetadataEditableMenuItem";
import { getStageColor } from "../../../utils/formattingFns";

const MetadataValue = ({
    value, 
    type,
}: {
    value: string | number | string[] | MetadataAssociatedFile[] | { [key: string]: any } | boolean,
    type: 'text' | 'textarea' | 'user' | 'tags' | 'array' | 'boolean' | 'associated_files' | 'json' | 'state',
}) => {
    if (type === 'text' || type === 'textarea') {
        return (
            <p className="text-default">{value ? value as string | number : 'No value'}</p>
        )
    } else if (type === 'user') {
        if (value) {
            return (
                <div className="flex justify-start items-center gap-2 py-1 px-3 border border-outline rounded-2xl">
                    <div className="w-[14px h-[14px] text-default"><UserIcon/></div>
                    <p className="text-default">{value as string}</p>
                </div>
            )
        } else {
            return (
                <p className="text-default">No user</p>
            )
        }
        
    } else if (type === 'array' || type === 'tags') {
        const arrayValue = value as string[];
        if (arrayValue.length === 0) {
            return (
                <p className="text-default">No value</p>
            )
        } else {
            return (
                <div className="w-full p-2 border border-outline rounded-lg">
                    <div className="flex justify-start items-center gap-3 w-full flex-wrap max-h-[250px] overflow-y-auto">
                    {arrayValue.map(val => (
                        <div className="py-1 px-4 rounded-2xl bg-secondary">
                            <p className="font-medium text-primary text-[12px]">{val}</p>
                        </div>
                    ))}
                    </div>
                </div>
            )
        }
    } else if (type === 'boolean') {
        const booleanValue = value as boolean;
        return (
            <div className={`py-1 px-4 rounded-2xl ${booleanValue === true ? 'bg-[#E3EDBB] text-[#3C7828]' : 'bg-[#FFEBEB] text-[#941E1E]'}`}>
                <p className="font-medium text-[12px]">{booleanValue === true ? 'True' : 'False'}</p>
            </div>
        )
    } else if (type === 'json') {
        return (
            <ExpandJson json={value}/>
        )
    } else if (type === 'associated_files') {
        const associatedFiles = value as MetadataAssociatedFile[];
        return (
            <div className="flex flex-col gap-6 justify-start items-start w-full">
                {associatedFiles.map(file => (
                    <SelectedItem 
                        type={file.type}
                        name={file.name}
                        hash={file.hash}
                        metadata={file.size}
                    />
                ))}
            </div>
        )
    } else if (type === 'state') {
        if (!value) {
            return (
                <p className="text-default">Not available</p>
            ) 
        } else {
            const { label, background } = getStageColor(value as string);
            return (
                <div style={{backgroundColor: background}} className={`py-1 px-4 rounded-2xl`}>
                    <p style={{color: label}} className="font-medium text-[12px]">{value as string}</p>
                </div>
            )
        }
    } else {
        return <></>
    }
}

const MetadataValueContainer = ({
    label,
    colspan,
    children,
}: {
    label: string,
    colspan: number,
    children: ReactNode,
}) => {
    return (
        <div className={`w-full flex flex-col gap-1 justify-start items-start ${colspan === 2 ? 'col-span-2' : ''}`}>
            <label className="font-medium text-[12px]">{label}</label>
            {children}
        </div>
    )
}

export default function MetadataMenuItem({
    metadataItem,
    index,
    updateFunc,
}: {
    metadataItem: MetadataInfo | MetadataStage | MetadataGroupInfo,
    index: number,
    updateFunc?: (name: string, value: any) => Promise<void>,
}) {
    if ("function_name" in metadataItem) {
        const { function_name, function_params } = metadataItem as MetadataStage;
        return (
            <div className={`flex flex-col justify-start items-start ${function_params.length > 0 ? 'gap-6' : 'gap-2'} mb-6 w-full col-span-2`}>
                <p className="text-default font-semibold">{`${index+1}. ${function_name}`}</p>
                {function_params.length > 0 ? (
                    <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8 w-full">
                    {function_params.map((param, i) => {
                        const { label, value, colspan, type } = param;
                        return (
                            <MetadataValueContainer
                                key={i}
                                label={label}
                                colspan={colspan}
                            >
                                <MetadataValue 
                                    value={value}
                                    type={type}
                                />
                            </MetadataValueContainer>
                        )
                    })}
                    </div>
                ) : (
                    <p className="text-disable">No params</p>
                )}
            </div>
        )
    } else if ("groupMetadata" in metadataItem) {
        const { param, groupMetadata } = metadataItem as MetadataGroupInfo;
        return (
            <div className="flex flex-col justify-start items-start gap-6 mb-6 w-full col-span-2">
                <p className="text-default font-semibold">{param}</p>
                {groupMetadata.length > 0 ? (
                    <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8 w-full">
                    {groupMetadata.map((metadata, i) => {
                        const { label, value, colspan, type } = metadata;
                        return (
                            <MetadataValueContainer
                                key={i}
                                label={label}
                                colspan={colspan}
                            >
                                <MetadataValue 
                                    value={value}
                                    type={type}
                                />
                            </MetadataValueContainer>
                        )
                    })}
                    </div>
                ) : (
                    <p className="text-disable">No params</p>
                )}
            </div>
        )
    } else {
        const { label, value, type, editableParam, colspan } = metadataItem as MetadataInfo;
        if (editableParam !== undefined && updateFunc) {
            return (
                <MetadataEditableMenuItem
                    metadataInfoItem={metadataItem}
                    updateFunc={updateFunc}
                >
                    <MetadataValue 
                        value={value}
                        type={type}
                    />
                </MetadataEditableMenuItem>
            )
        } else {
            return (
                <MetadataValueContainer
                    label={label}
                    colspan={colspan}
                >
                    <MetadataValue 
                        value={value}
                        type={type}
                    />
                </MetadataValueContainer>
            )
        }
    }
}